import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { CONTACT, NAV, IMAGES } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Danke — Sie sind angemeldet.");
      setEmail("");
    } catch {
      toast.error("Bitte prüfen Sie Ihre E-Mail-Adresse.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative bg-[color:var(--ink)] text-[color:var(--cream)]">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-24 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <img src={IMAGES.logo} alt="Classic Data Berlin" className="h-12 w-12 rounded-sm bg-[color:var(--cream)] p-1" />
              <div>
                <div className="font-serif text-2xl">Classic Data <span className="italic">Berlin</span></div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-white/60">Bewertungspartner</div>
              </div>
            </div>
            <p className="mt-6 font-serif text-3xl leading-[1.15] max-w-md">
              Unabhängige Wertgutachten <span className="italic">für Oldtimer & Youngtimer</span> — seit 2002.
            </p>
            <form onSubmit={submit} className="mt-8 flex gap-2 max-w-md" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email"
                placeholder="Ihre E-Mail für unseren Newsletter"
                className="flex-1 bg-transparent border-b border-white/40 focus:border-white outline-none py-3 text-sm placeholder:text-white/50"
              />
              <button data-testid="newsletter-submit" disabled={loading} className="text-xs tracking-[0.2em] uppercase border-b border-white/40 hover:border-white pb-3 transition-colors disabled:opacity-50">
                {loading ? "…" : "Abonnieren"}
              </button>
            </form>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[10px] tracking-[0.24em] uppercase text-white/50 mb-5">Navigation</div>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} data-testid={`footer-nav-${n.to.replace(/\//g, "") || "home"}`} className="text-[15px] hover:text-white/70 link-underline">{n.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="text-[10px] tracking-[0.24em] uppercase text-white/50 mb-5">Kontakt</div>
            <ul className="space-y-4 text-[15px]">
              <li className="flex items-start gap-3"><MapPin className="h-4 w-4 mt-1 shrink-0" /><span>{CONTACT.street}<br />{CONTACT.city}</span></li>
              <li className="flex items-start gap-3"><Phone className="h-4 w-4 mt-1 shrink-0" /><a href={`tel:${CONTACT.phoneRaw}`} className="hover:text-white/70">{CONTACT.phone}</a></li>
              <li className="flex items-start gap-3"><Mail className="h-4 w-4 mt-1 shrink-0" /><a href={`mailto:${CONTACT.email}`} className="hover:text-white/70">{CONTACT.email}</a></li>
              <li className="flex items-start gap-3"><Clock className="h-4 w-4 mt-1 shrink-0" /><span>{CONTACT.hours}<br /><span className="text-white/60">{CONTACT.hoursNote}</span></span></li>
            </ul>
            <div className="mt-8 border border-white/15 aspect-[16/9] overflow-hidden">
              <iframe
                title="Google Maps"
                src={`https://www.google.com/maps?q=${CONTACT.mapsQuery}&output=embed`}
                className="w-full h-full grayscale"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/15 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="text-xs text-white/60">© {new Date().getFullYear()} KFZ-Sachverständigenbüro Björn Linkowitz · Classic Data Berlin</div>
          <div className="flex gap-6 text-xs text-white/60">
            <Link to="/impressum" data-testid="footer-impressum" className="hover:text-white">Impressum</Link>
            <Link to="/datenschutz" data-testid="footer-datenschutz" className="hover:text-white">Datenschutz</Link>
            <Link to="/agb" data-testid="footer-agb" className="hover:text-white">AGBs</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
